import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { BankAccountOverview, BankAccountOverviewItem, BankCurrency } from "@/api/types";
import { money } from "@/components/format";

export type BankOverviewStage = "load" | "validate" | "reconcile" | "review";

export function useBankAccountOverview(
  hotelId: string,
  period: string,
  bankId: string,
  currency: string,
  enabled = true,
) {
  const selectedCurrency = currency === "ARS" || currency === "USD" ? currency : undefined;
  return useQuery({
    queryKey: ["bank-account-overview", hotelId, period, bankId, selectedCurrency],
    queryFn: (): Promise<BankAccountOverview> => responseData(apiClient.GET("/api/v1/banking/account-overview", {
      params: { query: { hotelId, period, bankId: bankId || undefined, currency: selectedCurrency } },
    })),
    enabled: enabled && Boolean(hotelId),
  });
}

const stageStateLabel = (state: BankAccountOverviewItem["stages"][BankOverviewStage]) => {
  if (state === "COMPLETE") return "Completa";
  if (state === "PARTIAL") return "Parcial";
  if (state === "ACTION_REQUIRED") return "Requiere atención";
  return "Sin iniciar";
};

const missingLoadLabel = (account: BankAccountOverviewItem) => {
  const missing = [
    account.loads.extractStatus === null ? "Extracto" : "",
    account.loads.ledgerStatus === null ? "Libro Mayor" : "",
  ].filter(Boolean);
  return missing.length ? `Falta ${missing.join(" y ")}` : "Sin cargas confirmadas";
};

const balanceValue = (value: string | null, currency: BankCurrency) => value === null ? "Dato faltante" : money(value, currency);

function StageMark({ state }: { state: BankAccountOverviewItem["stages"][BankOverviewStage] }) {
  return <span className={`bank-account-stage-mark bank-account-stage-mark--${state.toLowerCase()}`} aria-label={stageStateLabel(state)}>
    {state === "COMPLETE" ? "✓" : state === "ACTION_REQUIRED" ? "!" : state === "PARTIAL" ? "◐" : "—"}
  </span>;
}

function AccountLine({ account, activeStage, href }: { account: BankAccountOverviewItem; activeStage: BankOverviewStage; href: string }) {
  const reconciledCount = account.reconciliation.auto + account.reconciliation.approved + account.reconciliation.adjusted;
  const loadDetail = account.loads.activeCount
    ? `${account.loads.activeCount} carga${account.loads.activeCount === 1 ? "" : "s"} en proceso`
    : account.loads.previewCount
      ? `${account.loads.previewCount} por confirmar`
      : account.loads.errorCount
        ? `${account.loads.errorCount} error${account.loads.errorCount === 1 ? "" : "es"}`
        : account.stages.load === "COMPLETE"
          ? "Extracto y Mayor confirmados"
          : missingLoadLabel(account);
  const stageClass = (stage: BankOverviewStage) => stage === activeStage ? " is-active" : "";
  return <Link className="bank-account-line" to={href} aria-label={`Abrir ${account.bankName} ${account.currency}`}>
    <header className="bank-account-line__identity">
      <h3>{account.bankName} <em>· {account.currency}</em></h3>
      {account.historical && <span className="status status--warning">Histórica</span>}
    </header>
    <div className={`bank-account-line__stage${stageClass("load")}`}>
      <StageMark state={account.stages.load} />
      <span><small>Carga</small><strong>{loadDetail}</strong><em>{account.loads.extractRecords} extracto · {account.loads.ledgerRecords} Mayor</em></span>
    </div>
    <div className={`bank-account-line__stage${stageClass("validate")}`}>
      <StageMark state={account.stages.validate} />
      <span><small>Validación</small><strong>{account.movements.bank + account.movements.ledger} registros</strong><em>{account.movements.bank} movimientos · {account.movements.ledger} asientos</em></span>
    </div>
    <div className={`bank-account-line__stage${stageClass("reconcile")}`}>
      <StageMark state={account.stages.reconcile} />
      <span><small>Conciliación</small><strong>{account.reconciliation.processed ? `${reconciledCount} conciliadas` : account.stages.validate === "COMPLETE" ? "Lista para conciliar" : "Bloqueada"}</strong><em>{account.reconciliation.suggested} sugeridas · {account.reconciliation.unmatchedOrigin + account.reconciliation.unmatchedCounterpart} sin coincidencia</em></span>
    </div>
    <div className={`bank-account-line__stage bank-account-line__stage--balance${stageClass("review")}`}>
      <StageMark state={account.stages.review} />
      <span><small>Saldo y revisión</small><strong className="mono">{balanceValue(account.balance.reconciledBalance, account.currency)}</strong><em>{account.balance.difference === null ? "Diferencia: dato faltante" : `Diferencia ${money(account.balance.difference, account.currency)}`} · {account.reconciliation.observationCount} observaciones</em></span>
    </div>
    <span className="bank-account-line__open" aria-hidden="true">→</span>
  </Link>;
}

export function BankAccountsOverview({ data, activeStage }: { data: BankAccountOverview; activeStage: BankOverviewStage }) {
  const location = useLocation();
  const accountHref = (account: BankAccountOverviewItem) => {
    const query = new URLSearchParams({ bankId: account.bankId, currency: account.currency });
    return `${location.pathname}?${query}`;
  };
  return <section className="bank-accounts-overview" aria-label="Cuentas del hotel">
    <div className="bank-account-lines" aria-label="Estado por cuenta bancaria">
      {data.accounts.map((account) => <AccountLine key={account.accountId} account={account} activeStage={activeStage} href={accountHref(account)} />)}
    </div>
    {!data.accounts.length && <div className="bank-accounts-overview__empty"><strong>No hay cuentas para este alcance.</strong><span>Configurá una cuenta bancaria o cambiá los filtros del período.</span></div>}
  </section>;
}
